// FC246/1275: pure helpers for the guarded, single-row Quick Intake UI.
export const QUICK_PENDING_STORAGE_KEY = "trainer_quick_intake_pending_v1";

// Playable-card intake accepts every catalogue category (Pokemon, Trainer, Energy).
// Eligibility is about verified *print identity*, not competition legality or type.
export const exactCatalogueEligibilityReason = (card) => {
  if (!Number.isSafeInteger(Number(card?.id)) || Number(card?.id) <= 0 ||
      !String(card?.id ?? "").trim()) return "Catalogue ID missing or invalid";
  if (!String(card?.name_and_number || "").trim() ||
      !/\b\d{1,4}\s*\/\s*\d{1,4}\b/.test(String(card?.name_and_number || "")))
    return "Numbered catalogue print missing or incomplete";
  if (!String(card?.set_name || "").trim()) return "Catalogue set missing";
  return "";
};

export const isExactCatalogueCard = (card) => !exactCatalogueEligibilityReason(card);
// Compatibility exports: #1275–#1285 import the old Trainer-named helpers.
// Both now validate exact catalogue identity for all card types.
export const exactTrainerEligibilityReason = exactCatalogueEligibilityReason;
export const isExactTrainerCard = isExactCatalogueCard;

// Shared input contract used by the AI paste UI; restore the expected export so
// the React production build can resolve QuickTrainerBulkPaste's import.
export const validQuickUnitCost = (value) => {
  const text = String(value ?? "").trim();
  return /^\d+(?:\.\d{1,4})?$/.test(text) &&
    Number.isFinite(Number(text)) && Number(text) <= 999999.9999;
};

export const positiveQuantity = (value) => {
  const candidate = String(value ?? "").trim();
  if (!/^[1-9][0-9]*$/.test(candidate)) return null;
  const number = Number(candidate);
  return Number.isSafeInteger(number) && number <= 2147483647 ? number : null;
};

export const quickBatchTotals = (rows = []) => {
  const unique = new Set();
  let staged = 0;
  let received = 0;
  let voided = 0;
  let voidedRows = 0;
  rows.forEach((row) => {
    const total = Number(row.quantity) || 0;
    const quantity = Math.max(0, total);
    if (row.voided_at) {
      voided += quantity;
      voidedRows += 1;
      return;
    }
    const applied = Math.max(0, Math.min(quantity, Number(row.quantity_applied) || 0));
    staged += quantity - applied;
    received += applied;
    unique.add(`${row.card_name || ""}::${row.card_variant || "Normal"}`);
  });
  return { entries: rows.length - voidedRows, distinct: unique.size, staged, received,
    total: staged + received, voided, voidedRows };
};

export const makeQuickReceiptPayload = ({ card, variant, quantity, details, requestId }) => ({
  request_id: requestId,
  catalog_card_id: Number(card.id),
  name_and_number: card.name_and_number,
  set_name: card.set_name,
  card_variant: variant,
  card_condition: "Near Mint",
  quantity,
  inventory_apply_mode: "stage",
  single_allocation_quantity: 0,
  playset_allocation_quantity: 0,
  source_type: details.sourceType || "bulk_lot",
  unit_cost: details.unitCost === "" ? "0" : String(details.unitCost),
  source_reference: details.sourceReference.trim() || null,
  storage_location: details.storageLocation.trim() || null,
  notes: details.notes.trim() || null,
});

export const createQuickRequestId = () => {
  const uuid = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
  return `quick-${uuid}`;
};

export const readPendingQuickReceipt = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(QUICK_PENDING_STORAGE_KEY) || "null");
    return stored && positiveQuantity(stored.batchId) &&
      /^[A-Za-z0-9._:-]{8,96}$/.test(stored.payload?.request_id || "") ? stored : null;
  } catch (_) {
    return null;
  }
};

export const persistPendingQuickReceipt = (pending) => {
  try {
    if (pending) window.localStorage.setItem(QUICK_PENDING_STORAGE_KEY, JSON.stringify(pending));
    else window.localStorage.removeItem(QUICK_PENDING_STORAGE_KEY);
  } catch (_) {
    // Unavailable browser storage must not prevent use of the in-memory retry token.
  }
};

// Stable precondition for the guarded #1277 physical receipt endpoint.
// Rows removed from staging are intentionally not part of the snapshot.
export const quickFinaliseSnapshot = (rows = []) => rows
  .filter((row) => !row.voided_at)
  .map((row) => ({ id: Number(row.id), revision: Number(row.revision || 1) }))
  .sort((a, b) => a.id - b.id);
